/**
 * Certificado odontológico IESS: el formato que se envía para validar reposos en el IESS.
 * Tiene campos fijos y listas cerradas (tipo de contingencia, diagnóstico con CIE-10, síntomas, reposo de 1 a 6 días).
 * Los datos del paciente y del profesional se congelan al firmar.
 */
import { DIAGNOSTICOS_IESS } from "./certificado-iess-lista.ts";
import { finReposo } from "./certificados.ts";

export { DIAGNOSTICOS_IESS };

export const CONTINGENCIAS_IESS = [
  "ENFERMEDAD GENERAL", "ENFERMEDAD CATASTRÓFICA", "MATERNIDAD", "ACCIDENTE GRAVE DEBIDAMENTE CERTIFICADO",
  "PRESUNCIÓN DE ACCIDENTE DE TRABAJO", "PRESUNCIÓN DE ENFERMEDAD PROFESIONAL U OCUPACIONAL",
] as const;
export const SINTOMAS_IESS = ["DOLOR AGUDO", "DOLOR CRÓNICO", "NO REFIERE"] as const;
export const SI_NO_IESS = ["SI", "NO"] as const;
export const TELETRABAJO_IESS = ["SI (PARCIAL - NO HABLAR)", "NO"] as const;
export const SERVICIO_IESS = "CONSULTA EXTERNA";
export const MAX_DIAS_REPOSO_IESS = 6;
const UNIDADES = ["", "UN DÍA (1)", "DOS DÍAS (2)", "TRES DÍAS (3)", "CUATRO DÍAS (4)", "CINCO DÍAS (5)", "SEIS DÍAS (6)"];
export const totalReposoIess = (dias: number) => (dias >= 1 && dias <= MAX_DIAS_REPOSO_IESS ? UNIDADES[dias] : "NO APLICA");

export const diagnosticoIess = (codigo: string) => DIAGNOSTICOS_IESS.find((d) => d[0] === codigo)?.[1] ?? null;

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
/** «3 Octubre 2026», como va en el encabezado del formato. */
export const fechaIess = (f: string) => { const [a, m, d] = f.split("-").map(Number); return `${d} ${MESES[m - 1]} ${a}`; };

export interface DatosIess {
  fecha: string; ciudad: string;
  apellidos: string; nombres: string; domicilio: string; contacto: string; ci: string; hc: number | string; email: string;
  empresa: string; puesto: string;
  contingencia: string; cie10: string; sintomas: string; enfermedad: string; tratamiento: string;
  aislamiento: string; teletrabajo: string; reposo: string; dias: number; desde: string | null;
}

export function problemasIess(d: Pick<DatosIess, "fecha" | "empresa" | "puesto" | "contingencia" | "cie10" | "sintomas" | "enfermedad" | "tratamiento" | "aislamiento" | "teletrabajo" | "reposo" | "dias" | "desde">, hoy: string): string[] {
  const p: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.fecha)) p.push("La fecha no es válida.");
  else if (d.fecha > hoy) p.push("La fecha no puede ser futura.");
  if (!d.empresa.trim()) p.push("Indica la empresa o institución.");
  if (!d.puesto.trim()) p.push("Indica el puesto de trabajo.");
  if (!(CONTINGENCIAS_IESS as readonly string[]).includes(d.contingencia)) p.push("Elige el tipo de contingencia.");
  if (!diagnosticoIess(d.cie10)) p.push("Elige el diagnóstico de la lista.");
  if (!(SINTOMAS_IESS as readonly string[]).includes(d.sintomas)) p.push("Elige si presenta síntomas.");
  if (!(SI_NO_IESS as readonly string[]).includes(d.enfermedad)) p.push("Indica si hay enfermedad (Sí o No).");
  if (!d.tratamiento.trim()) p.push("Describe el tratamiento realizado.");
  if (!(SI_NO_IESS as readonly string[]).includes(d.aislamiento)) p.push("Indica si hay aislamiento.");
  if (!(TELETRABAJO_IESS as readonly string[]).includes(d.teletrabajo)) p.push("Indica si puede hacer teletrabajo.");
  if (!(SI_NO_IESS as readonly string[]).includes(d.reposo)) p.push("Indica si hay reposo.");
  else if (d.reposo === "SI") {
    if (!Number.isInteger(d.dias) || d.dias < 1 || d.dias > MAX_DIAS_REPOSO_IESS) p.push(`El reposo debe ser de 1 a ${MAX_DIAS_REPOSO_IESS} días.`);
    if (!d.desde || !/^\d{4}-\d{2}-\d{2}$/.test(d.desde)) p.push("Indica desde qué fecha rige el reposo.");
    else if (d.desde < d.fecha) p.push("El reposo no puede empezar antes de la fecha del certificado.");
    else if ((Date.parse(d.desde) - Date.parse(d.fecha)) / 86400000 > 1) p.push("El reposo debe empezar el día de la atención o el siguiente.");
  } else if (d.dias !== 0) p.push("Sin reposo, los días deben ser cero.");
  return p;
}

export const hastaIess = (d: Pick<DatosIess, "reposo" | "desde" | "dias">) => (d.reposo === "SI" && d.desde ? finReposo(d.desde, d.dias) : null);

/** Las líneas del certificado, en el orden del formato (se usa para imprimir y para el texto que se guarda). */
export function lineasIess(d: DatosIess): [etiqueta: string, valor: string][] {
  const dx = diagnosticoIess(d.cie10) ?? "";
  const hasta = hastaIess(d);
  return [
    ["APELLIDOS", d.apellidos], ["NOMBRES", d.nombres], ["DOMICILIO", d.domicilio], ["NÚMERO DE CONTACTO", d.contacto], ["C.I", d.ci],
    ["HISTORIA CLÍNICA", `# ${d.hc}`], ["EMAIL", d.email], ["EMPRESA / INSTITUCIÓN", d.empresa], ["PUESTO DE TRABAJO", d.puesto],
    ["SERVICIO", SERVICIO_IESS], ["TIPO DE CONTINGENCIA", d.contingencia], ["DIAGNÓSTICO", dx], ["CIE 10", d.cie10],
    ["PRESENTA SÍNTOMAS", d.sintomas], ["ENFERMEDAD", d.enfermedad], ["DESCRIPCIÓN TRATAMIENTO", d.tratamiento],
    ["AISLAMIENTO", d.aislamiento], ["TELETRABAJO", d.teletrabajo], ["REPOSO", d.reposo],
    ["TOTAL REPOSO MÉDICO", totalReposoIess(d.reposo === "SI" ? d.dias : 0)],
    ["DESDE", d.desde && hasta ? fechaIess(d.desde) : ""], ["HASTA", hasta ? fechaIess(hasta) : ""],
  ];
}

export function textoCertificadoIess(d: DatosIess): string {
  return [`${d.ciudad ? `${d.ciudad}, ` : ""}${fechaIess(d.fecha)}`, "CERTIFICADO ODONTOLÓGICO", lineasIess(d).map(([e, v]) => `${e}: ${v}`).join("\n")].join("\n\n");
}
